import { ApiHttpError } from '@/api/http'
import { authService } from '@/services/auth.service'
import { userService } from '@/services/user.service'
import type { RegisterData, User, UserRole } from '@/types/user'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const storedUser = localStorage.getItem('currentUser')
  if (storedUser) {
    try {
      currentUser.value = JSON.parse(storedUser)
    } catch (_e) {
      localStorage.removeItem('currentUser')
      localStorage.removeItem('accessToken')
    }
  }

  const isAuthenticated = computed(() => !!currentUser.value && !!accessToken.value)
  const userRole = computed<UserRole | 'visitor'>(() => currentUser.value?.role ?? 'visitor')

  async function login(credentials: { email: string; password: string }): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      console.log('[BdC-AuthStore] Tentative de connexion pour:', credentials.email)
      const response = await authService.login(credentials)
      console.log('[BdC-AuthStore] Réponse login reçue:', response)

      currentUser.value = response.user
      accessToken.value = response.accessToken

      localStorage.setItem('currentUser', JSON.stringify(response.user))
      localStorage.setItem('accessToken', response.accessToken)

      console.log(
        '[BdC-AuthStore] Utilisateur connecté:',
        `${response.user.firstName} ${response.user.lastName}`,
        'Role:',
        response.user.role
      )

      return true
    } catch (_err) {
      console.error('[BdC-AuthStore] Erreur lors de la connexion:', _err)
      if (_err instanceof ApiHttpError) {
        error.value = _err.message
      } else {
        error.value = 'Email ou mot de passe incorrect.'
      }
      return false
    } finally {
      isLoading.value = false
    }
  }

  function logout() {
    console.log('[BdC-AuthStore] Déconnexion en cours...')
    currentUser.value = null
    accessToken.value = null
    localStorage.removeItem('currentUser')
    localStorage.removeItem('accessToken')

    // Invalider le cache des préférences utilisateur
    // Utiliser setTimeout pour éviter les problèmes d'import circulaire
    setTimeout(async () => {
      try {
        const { useUserPreferencesStore } = await import('@/stores/userPreferences')
        const preferencesStore = useUserPreferencesStore()
        preferencesStore.invalidateCache()
      } catch (_err) {
        // Ignorer si le store n'est pas encore chargé
      }
    }, 0)
  }

  const hasRole = (requiredRole: UserRole): boolean => {
    if (!currentUser.value) return false
    const rolesHierarchy: UserRole[] = ['visitor', 'student', 'researcher', 'admin']
    const requiredRoleIndex = rolesHierarchy.indexOf(requiredRole)
    const userRoleIndex = rolesHierarchy.indexOf(currentUser.value.role)
    return userRoleIndex >= requiredRoleIndex
  }

  async function register(data: RegisterData): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      console.log('[BdC-AuthStore] Tentative d’inscription pour:', data.email)
      const response = await authService.register(data)
      console.log('[BdC-AuthStore] Réponse register reçue:', response)

      currentUser.value = response.user
      accessToken.value = response.accessToken
      localStorage.setItem('currentUser', JSON.stringify(response.user))
      localStorage.setItem('accessToken', response.accessToken)
      console.log(
        '[BdC-AuthStore] Utilisateur inscrit et connecté:',
        `${response.user.firstName} ${response.user.lastName}`
      )
      console.log('[BdC-AuthStore] Inscription réussie pour:', data.email)
      return true
    } catch (err) {
      console.error('[BdC-AuthStore] Erreur lors de l’inscription:', err)
      if (err instanceof ApiHttpError) {
        error.value = err.message
      } else if (err instanceof Error) {
        error.value = err.message
      } else {
        error.value = 'Une erreur inconnue est survenue lors de la création du compte.'
      }
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function forgotPassword(data: { email: string }): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      await authService.forgotPassword(data)
      return true
    } catch (err: any) {
      error.value = err.message || 'Erreur serveur'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function resetPassword(data: { token: string; password: string }): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      await authService.resetPassword(data)
      return true
    } catch (err: any) {
      error.value = err.message || 'Erreur serveur'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function verifyEmail(token: string): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      await authService.verifyEmail(token)
      return true
    } catch (err: any) {
      error.value = err.message || 'Erreur serveur'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    isLoading.value = true
    error.value = null
    if (!currentUser.value) {
      error.value = 'Utilisateur non connecté'
      isLoading.value = false
      return false
    }
    try {
      await userService.updatePassword(currentUser.value.id, currentPassword, newPassword)
      return true
    } catch (err: any) {
      error.value = err.message || 'Erreur serveur'
      return false
    } finally {
      isLoading.value = false
    }
  }

  return {
    currentUser,
    isLoading,
    error,
    isAuthenticated,
    userRole,
    login,
    logout,
    hasRole,
    accessToken,
    register,
    forgotPassword,
    resetPassword,
    verifyEmail,
    changePassword,
  }
})
